/**
 * Reativar BU, showing what comes back with it.
 *
 * The counts are the same ones the exclusion dialog showed, because disabling a BU never rewrote
 * its children: they kept their own state. So this is a faithful list of what returns — and what
 * was individually turned off before stays off.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { getTenantCascadePreview } from '../../Services';
import type { Tenant } from '../BUPlatformList/BUPlatformList.types';
import { CascadeSummary } from '../CascadeSummary/CascadeSummary';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const textStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const noteStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface RestoreBUModalProps {
  open: boolean;
  tenant: Tenant;
  isRestoring: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (tenant: Tenant) => void;
}

export const RestoreBUModal = ({
  open,
  tenant,
  isRestoring,
  error,
  onClose,
  onConfirm,
}: RestoreBUModalProps): JSX.Element => {
  const preview = useQuery({
    queryKey: ['tenant-cascade-preview', tenant.slug],
    queryFn: () => getTenantCascadePreview(tenant.slug),
    enabled: open,
  });

  const content = (
    <div className={bodyStyle}>
      <p className={textStyle}>
        Reativar <span className={slugStyle}>{tenant.slug}</span> traz de volta a BU e tudo que
        ficou guardado com ela:
      </p>

      <CascadeSummary
        preview={preview.data}
        isLoading={preview.isLoading}
        error={preview.error?.message ?? null}
      />

      <p className={noteStyle}>
        Itens que já estavam desativados individualmente antes da exclusão continuam desativados —
        reativar a BU não liga nada que alguém tenha desligado de propósito.
      </p>

      {error !== null ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='restore-bu-confirm-button'
          variant='primary'
          text={isRestoring ? 'Reativando...' : 'Reativar BU'}
          disabled={isRestoring}
          onClick={() => {
            onConfirm(tenant);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Reativar business unit'
      content={content}
      maxWidth='560px'
      hasButtonClose
      position='center'
    />
  );
};
